export type ObservationTrendKind =
  | 'BP_SYSTOLIC'
  | 'BP_DIASTOLIC'
  | 'HEART_RATE'
  | 'SPO2'
  | 'BLOOD_GLUCOSE'
  | 'TEMPERATURE'
  | 'WEIGHT'
  | 'PAIN_SCORE'
  | 'RESPIRATORY_RATE'
  | 'BRADEN_SCORE'
  | 'FALLS_RISK_SCORE';

export type ObservationTrendPoint = {
  recordedAt: string;
  kind: ObservationTrendKind;
  value: number;
};

export type ObservationThresholdBand = {
  kind: ObservationTrendKind;
  low?: number;
  high?: number;
  unit: string;
  label: string;
};

export type WeightLossFlag = {
  triggered: boolean;
  percentLoss: number;
  baselineValue: number | null;
  latestValue: number | null;
};

export const OBSERVATION_THRESHOLD_BANDS: Record<ObservationTrendKind, ObservationThresholdBand> = {
  BP_SYSTOLIC: { kind: 'BP_SYSTOLIC', high: 160, unit: 'mmHg', label: 'Systolic BP' },
  BP_DIASTOLIC: { kind: 'BP_DIASTOLIC', high: 100, unit: 'mmHg', label: 'Diastolic BP' },
  HEART_RATE: { kind: 'HEART_RATE', low: 50, high: 110, unit: 'bpm', label: 'Heart rate' },
  SPO2: { kind: 'SPO2', low: 92, unit: '%', label: 'SpO2' },
  BLOOD_GLUCOSE: { kind: 'BLOOD_GLUCOSE', low: 4, high: 15, unit: 'mmol/L', label: 'BGL' },
  TEMPERATURE: { kind: 'TEMPERATURE', high: 38, unit: 'C', label: 'Temperature' },
  WEIGHT: { kind: 'WEIGHT', unit: 'kg', label: 'Weight' },
  PAIN_SCORE: { kind: 'PAIN_SCORE', high: 7, unit: '/10', label: 'Pain score' },
  RESPIRATORY_RATE: {
    kind: 'RESPIRATORY_RATE',
    low: 8,
    high: 24,
    unit: '/min',
    label: 'Respiratory rate',
  },
  BRADEN_SCORE: { kind: 'BRADEN_SCORE', low: 12, unit: 'score', label: 'Braden score' },
  FALLS_RISK_SCORE: { kind: 'FALLS_RISK_SCORE', high: 12, unit: 'score', label: 'Falls risk' },
};

export function getObservationThresholdBand(kind: ObservationTrendKind): ObservationThresholdBand {
  return OBSERVATION_THRESHOLD_BANDS[kind];
}

export function sortObservationTrendPoints(
  points: ObservationTrendPoint[],
): ObservationTrendPoint[] {
  return [...points].sort(
    (left, right) => new Date(left.recordedAt).getTime() - new Date(right.recordedAt).getTime(),
  );
}

export function calculateWeightLossFlag(
  points: ObservationTrendPoint[],
  thresholdPercent = 5,
): WeightLossFlag {
  const weightPoints = sortObservationTrendPoints(points).filter(
    (point) => point.kind === 'WEIGHT' && Number.isFinite(point.value),
  );

  if (weightPoints.length < 2) {
    return {
      triggered: false,
      percentLoss: 0,
      baselineValue: weightPoints[0]?.value ?? null,
      latestValue: weightPoints[0]?.value ?? null,
    };
  }

  const baselinePoint = weightPoints[0];
  const latestPoint = weightPoints[weightPoints.length - 1];
  if (!baselinePoint || !latestPoint) {
    return {
      triggered: false,
      percentLoss: 0,
      baselineValue: null,
      latestValue: null,
    };
  }

  const baselineValue = baselinePoint.value;
  const latestValue = latestPoint.value;
  const percentLoss =
    baselineValue > 0 ? Math.max(0, ((baselineValue - latestValue) / baselineValue) * 100) : 0;

  return {
    triggered: percentLoss >= thresholdPercent,
    percentLoss: Number(percentLoss.toFixed(1)),
    baselineValue,
    latestValue,
  };
}

export function isObservationOutsideBand(point: ObservationTrendPoint): boolean {
  const band = getObservationThresholdBand(point.kind);
  if (typeof band.low === 'number' && point.value < band.low) return true;
  if (typeof band.high === 'number' && point.value > band.high) return true;
  return false;
}
